import { IconButton } from "@mui/material";
import { useRef, useState } from "react";
import RecordRTC from "recordrtc";
import Iconify from "../Iconify";
import { VoiceAnimation } from "./voiceAnimation";

export default function SpeechToTextRTC({ setDescription }) {
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const silenceCheckerRef = useRef(null);
  const isStoppedRef = useRef(false); // prevents double stop

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new RecordRTC(stream, {
        type: "audio",
        mimeType: "audio/wav",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        sampleRate: 44100,
        timeSlice: 1000,
        onAudioProcessStarted: () => {
          setRecording(true);
        },
      });

      recorderRef.current = recorder;
      isStoppedRef.current = false;
      recorder.startRecording();

      // Silence Detection
      silenceCheckerRef.current = detectSilence(
        stream,
        () => stopRecording(true),
        2000,
        2
      );
    } catch (error) {
      console.error("Recording failed:", error);
    }
  };

  const stopRecording = async (fromSilence = false) => {
    if (!recorderRef.current || isStoppedRef.current) return;
    isStoppedRef.current = true;

    try {
      setRecording(false);

      // Clean audio context
      if (silenceCheckerRef.current?.close) {
        silenceCheckerRef.current.close();
        silenceCheckerRef.current = null;
      }

      recorderRef.current.stopRecording(async () => {
        const blob = recorderRef.current.getBlob();
        recorderRef.current = null;
        streamRef.current?.getTracks().forEach((track) => track.stop());

        const formData = new FormData();
        formData.append("audio", blob);
        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}transcribe`,
          {
            method: "POST",
            body: formData,
          }
        );

        const { transcript } = await response.json();
        console.log("Transcription:", transcript);
        setDescription(transcript);
      });
    } catch (err) {
      console.error("Stop recording failed:", err);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0px",
        justifyContent: "flex-end",
        position: "absolute",
        bottom: "5px",
        right: "3px",
      }}
    >
      {recording && <VoiceAnimation />}

      {!recording ? (
        <IconButton onClick={startRecording}>
          <Iconify
            icon="material-symbols:mic-rounded"
            sx={{ width: 30, height: 24 }}
          />
        </IconButton>
      ) : (
        <IconButton onClick={() => stopRecording(false)}>
          <Iconify icon="heroicons-solid:stop" sx={{ width: 30, height: 24 }} />
        </IconButton>
      )}
    </div>
  );
}

// === Helper: Silence Detection ===
function detectSilence(
  stream,
  onSilence,
  timeout = 2000,
  silenceThreshold = 2
) {
  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;

  const source = ctx.createMediaStreamSource(stream);
  source.connect(analyser);

  const data = new Uint8Array(analyser.fftSize);
  let silenceStart = performance.now();

  function loop() {
    analyser.getByteTimeDomainData(data);
    const volume =
      data.reduce((sum, v) => sum + Math.abs(v - 128), 0) / data.length;

    if (volume < silenceThreshold) {
      if (performance.now() - silenceStart > timeout) {
        onSilence();
        return;
      }
    } else {
      silenceStart = performance.now();
    }

    requestAnimationFrame(loop);
  }

  loop();
  return ctx; // So we can close it later
}
